import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import { useSelector } from 'react-redux'

import { useFlags } from '@lyrahealth-inc/shared-app-logic/src/features/flags/useFlags'
import { endOfSessionMetaData } from '@lyrahealth-inc/shared-app-logic/src/features/sessions/endOfSession/endOfSessionMetadata'
import { Appointment } from '@lyrahealth-inc/shared-app-logic/src/models/appointment/Appointment'
import { Episode } from '@lyrahealth-inc/shared-app-logic/src/models/episodes/Episodes'
import { FormMetadata } from '@lyrahealth-inc/shared-app-logic/src/models/formMetadata/FormMetadata'

import { useInitialEndOfSessionValues } from './useInitialEndOfSessionValues'
import { useInitialSessionDetails } from './useInitialSessionDetails'
import { useModalityOptions } from './useModalityOptions'
import {
  episodeTerminationStatuses,
  episodeTerminationStatusesEngageDA,
  getEndOfSessionConditions,
} from '../../../../common/constants/appConstants'
import { getClientUnclosedPastAppointments } from '../../../../data/lyraTherapy/clientSelectors'
import { getPaymentsDataAllData } from '../../../../data/payments/paymentsSelectors'
import { GetTransfersResponse } from '../../../../payments/data/transfersApi'
import { useClientAppointmentsData } from '../../../clients/clientDetails/data/hooks/useClientAppointmentsData'
import { useSelectedClientData } from '../../../clients/clientDetails/data/hooks/useSelectedClientData'
import { useEpisodesForSelectedClient } from '../../../episodes/data/hooks/useEpisodesForSelectedClient'

type UseCloseSessionMetadataParams = {
  appointmentToClose: Appointment | undefined
  selectedEpisode: Episode | undefined
  isProviderEngageDA: boolean
  isProviderCoach: boolean
  useEnhancedDiagnosisCapture: boolean
  shouldShowGuardianConsent: boolean
  nextSessionUsesHealthPlan: boolean
  calculatedSessionRate: string | number
  previousCharge?: GetTransfersResponse
  isDaFlexibleNote?: boolean
}

/**
 * Builds the base close-session FormMetadata (schema/uiSchema/initialValues) from
 * `endOfSessionMetaData`. This is the single source of truth for the close-session form,
 * shared by the EndOfSession screen and the unified DA Flexible Note flow.
 *
 * It owns metadata *construction* only: conditions list, termination statuses,
 * canEndEpisode, modality options, and the initial-value derivation. Eligibility-derived
 * "decision" inputs (rate, enhanced-diagnosis capture, guardian consent, health-plan usage)
 * are passed in by the caller, since EndOfSession and the flex flow source them differently
 * (inline vs `useCloseSessionEligibility`).
 *
 * The returned metadata is "bare" — callers apply any ICD10/attestation/specialty-care
 * `extendMetadata` chains themselves.
 */
export const useCloseSessionMetadata = ({
  appointmentToClose,
  selectedEpisode,
  isProviderEngageDA,
  isProviderCoach,
  useEnhancedDiagnosisCapture,
  shouldShowGuardianConsent,
  nextSessionUsesHealthPlan,
  calculatedSessionRate,
  previousCharge,
  isDaFlexibleNote = false,
}: UseCloseSessionMetadataParams): { metadata: FormMetadata } => {
  const intl = useIntl()
  const { isDAProviderMigrated } = useFlags()
  const { data: clientDetails } = useSelectedClientData()
  const { data: episodes } = useEpisodesForSelectedClient()
  const { data: allAppointments } = useClientAppointmentsData()
  const pastAppointmentsToClose = useSelector(getClientUnclosedPastAppointments)
  const pastPayments = useSelector(getPaymentsDataAllData)
  const modalityOptions = useModalityOptions()

  const conditionsList = useMemo(
    () => getEndOfSessionConditions({ isProviderEngageDA, isDaFlexibleNote }),
    [isProviderEngageDA, isDaFlexibleNote],
  )

  const engageDAorLCxTerminationStatuses = isDAProviderMigrated
    ? episodeTerminationStatusesEngageDA
    : episodeTerminationStatuses
  const terminationStatuses =
    episodes && episodes.length > 1 && appointmentToClose?.sessionNumber === 1
      ? engageDAorLCxTerminationStatuses
      : engageDAorLCxTerminationStatuses.filter((status) => status.value !== 'booster')

  const canEndEpisode = isProviderEngageDA
    ? pastAppointmentsToClose?.filter((appt) => appt.episodeId === selectedEpisode?.id).length === 1
    : pastAppointmentsToClose?.every(
        (appt) =>
          appt.episodeId !== selectedEpisode?.id || appt.sessionNumber <= (appointmentToClose?.sessionNumber ?? 0),
      )

  const sessionDetailsInitialValues = useInitialSessionDetails(appointmentToClose, modalityOptions.enum)
  const endOfSessionValues = useInitialEndOfSessionValues(
    useEnhancedDiagnosisCapture,
    nextSessionUsesHealthPlan,
    previousCharge,
    selectedEpisode,
    allAppointments,
    pastPayments,
    conditionsList,
    clientDetails?.date_of_birth,
    appointmentToClose?.isChargeReversed,
  )

  const initialValues = {
    ...endOfSessionValues,
    ...sessionDetailsInitialValues,
  }

  const clientNameFallback = intl.formatMessage({
    defaultMessage: 'the client',
    description: 'Fallback text when client name is unavailable',
  })

  const metadata = endOfSessionMetaData({
    clientDetails: clientDetails || { first_name: clientNameFallback },
    conditionsList,
    terminationStatuses,
    canEndEpisode,
    shouldShowGuardianConsent,
    isProviderEngageDA,
    isProviderCoach,
    modalityOptions,
    calculatedSessionRate,
    useEnhancedDiagnosisCapture,
    ...initialValues,
  }) as FormMetadata

  return { metadata }
}
